import React from "react";
import styled from "styled-components/macro";
import { Stack } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSend } from "@duocards/pro-regular-svg-icons";
import { FullChat, getChatMaxMessages } from "./FullChat";
import { getTheme } from "styled/theme";
import { AiMessagesDepletedWarning } from "./AiMessagesDepletedWarning/AiMessagesDepletedWarning";
import { AssistantMode } from "./__generated__/AssistantMutation.graphql";
import { useRemainingAiMessages } from "./assistantHooks";
import { isDesktop } from "../../tools/device";
import SpeakingControls from "./SpeakingControls";
import SpeechToText from "components/speech/SpeechToText";
import { isMicrophoneSupported } from "tools/recorder";
import { InputIconButton, InputTextarea } from "./AssistantComponents";

const TxtAreaWrap = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  gap: 12px;
`;

const SIconButton = InputIconButton;

type Props = {
  inputRef: React.RefObject<HTMLTextAreaElement>;
  disabled: boolean;
  onSend: () => void;
  onTranscription: (text: string) => void;
  mode: AssistantMode;
  userMessagesCount: number;
};

const AssistantInput: React.FC<Props> = ({ inputRef, disabled, onSend, onTranscription, mode, userMessagesCount }) => {
  const remainingAiMessages = useRemainingAiMessages();
  const [isInputEmpty, setIsInputEmpty] = React.useState(true);
  const micSupported = isMicrophoneSupported();

  const handleInput = () => {
    const emptyNow = !inputRef.current?.value.trim();
    if (emptyNow !== isInputEmpty) {
      setIsInputEmpty(emptyNow);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && isDesktop()) {
      onSend();
    }
  };

  if (!remainingAiMessages) {
    return <AiMessagesDepletedWarning />;
  }

  if (userMessagesCount >= getChatMaxMessages(mode)) {
    return <FullChat mode={mode} />;
  }

  if (mode === "speaking") {
    return <SpeakingControls onTranscription={onTranscription} disabled={disabled} />;
  }

  if (mode !== "listenVocab" && mode !== "letsPractise") {
    return (
      <Stack direction="row" flexWrap="nowrap" alignItems="center">
        <TxtAreaWrap>
          <InputTextarea ref={inputRef} onKeyUp={handleKeyUp} onInput={handleInput} />
          {isInputEmpty && micSupported ? (
            <SpeechToText onTranscription={onTranscription} disabled={disabled} />
          ) : (
            <SIconButton onClick={onSend} disabled={disabled}>
              <FontAwesomeIcon icon={faSend} color={getTheme().palette.white} />
            </SIconButton>
          )}
        </TxtAreaWrap>
      </Stack>
    );
  }

  return null;
};

export default AssistantInput;
